import { SimpleValue } from "../interface/IDefine";
import { Validate } from "../common/enum/validate";
import { PhoneNumberUtil } from 'google-libphonenumber';
import { ValidationValue } from "../component/validation/IValidation";

export interface ValidationCallbackResult {
  name: string;
  code: ValidationErrorCode;
  message: string;
}

export interface ValidationError {
  code: ValidationErrorCode;
  text: string;
}

export const ValidationErrorCode = {
  IS_EMPTY: "isEmpty",
  INVALID_TYPE_AS_PHONE: "invalidTypeAsPhone",
  INVALID_TYPE_AS_POSTAL_CODE: "invalidTypeAsPostalCode",
  INVALID_TYPE_AS_NAMERIC: "invalidTypeAsNameric",
  INVALID_TYPE_AS_EMAIL: "invalidTypeAsEmail",
  INVALID_TYPE_AS_HIRAGANA: "invalidTypeAsHiragana",
  INVALID_TYPE_AS_KATAKANA: "invalidTypeAsKatakana",
  IS_SOME_EMPTY: "isSomeEmpty",
  IS_EVERY_EMPTY: "isEveryEmpty",
}
export type ValidationErrorCode = typeof ValidationErrorCode[keyof typeof ValidationErrorCode];

export const ValidationErrorMessage = {
  [ValidationErrorCode.IS_EMPTY]: "入力必須です。",
  [ValidationErrorCode.INVALID_TYPE_AS_PHONE]: "形式が正しくありません。",
  [ValidationErrorCode.INVALID_TYPE_AS_POSTAL_CODE]: "形式が正しくありません。",
  [ValidationErrorCode.INVALID_TYPE_AS_NAMERIC]: "半角の数字(0〜9)のみで入力してください。",
  [ValidationErrorCode.INVALID_TYPE_AS_EMAIL]: "メールアドレスを入力してください。",
  [ValidationErrorCode.INVALID_TYPE_AS_HIRAGANA]: "ひらがなで入力してください。",
  [ValidationErrorCode.INVALID_TYPE_AS_KATAKANA]: "カタカナで入力してください。",
  [ValidationErrorCode.IS_SOME_EMPTY]: "すべてを選択してください。",
  [ValidationErrorCode.IS_EVERY_EMPTY]: "入力必須です。",
  // [ValidationErrorCode.IS_EVERY_EMPTY]: "いずれかを選択してください。",
}
export type ValidationErrorMessage = typeof ValidationErrorMessage[keyof typeof ValidationErrorMessage];

export type ValidationCallback = (
  // value: ValidationCallbackValueArgument,
  value: ValidationValue,
  name: string,
  label?: string,
) => ValidationCallbackResult;

export interface ValidationProps {
  type: Validate, callback: ValidationCallback
}

export const prefix = (label?: string, suffix: string = "") => !!label ? `[${label}] は` + suffix : "";

export const getValidationErrorMessage = (code: ValidationErrorCode, name: string, label?: string, suffix: string = "") => {
  const messagePrefix = true ? "" : prefix(label);
  const message = messagePrefix + ValidationErrorMessage[code] + suffix;
  return { name, code, message };
}

const phoneUtil = PhoneNumberUtil.getInstance();

export const Validation: ValidationProps[] = [
  {
    type: Validate.NOT_EMPTY,
    callback: (value, name: string, label: string) => {
      if (
        value === null || value === undefined ||
        (typeof value === "string" && value.trim().length == 0) ||
        (typeof value === "number" && Number.isNaN(value))
      )
        return getValidationErrorMessage(ValidationErrorCode.IS_EMPTY, name, label);
      return null;
    }
  },
  {
    type: Validate.PHONE,
    callback: (value, name: string, label: string) => {
      if (!value || typeof value !== "string" && typeof value == "number")
        return getValidationErrorMessage(ValidationErrorCode.INVALID_TYPE_AS_NAMERIC, name, label);

      try {
        var phoneNumber = phoneUtil.parseAndKeepRawInput(value.toString(), 'JP');
        if (!phoneUtil.isValidNumberForRegion(phoneNumber, 'JP')) {
          return getValidationErrorMessage(ValidationErrorCode.INVALID_TYPE_AS_PHONE, name, label);
        }
      } catch(e) {
        return getValidationErrorMessage(ValidationErrorCode.INVALID_TYPE_AS_PHONE, name, label);
      }
      return null;
    }
  },
  {
    type: Validate.POSTAL_CODE,
    callback: (value, name: string, label: string) => {
      if (!value || typeof value !== "string" && typeof value == "number")
        return getValidationErrorMessage(ValidationErrorCode.INVALID_TYPE_AS_NAMERIC, name, label);
      const reg = new RegExp(/^[0-9]{3}-[0-9]{4}$/);
      if (!reg.test(value.toString()))
        return getValidationErrorMessage(ValidationErrorCode.INVALID_TYPE_AS_POSTAL_CODE, name, label);
      return null;
    }
  },
  {
    type: Validate.EMAIL,
    callback: (value, name: string, label: string) => {
      const reg = new RegExp(/^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/);
      if (!value || !reg.test(value.toString()))
        return getValidationErrorMessage(ValidationErrorCode.INVALID_TYPE_AS_EMAIL, name, label);
      return null;
    }
  },
  {
    type: Validate.SOME,
    callback: (value, name: string, label?: string) => {
      if (typeof value !== "object" || Array.isArray(value)) return null;
      let errorLabels: string[] = [];
      const keys = Object.keys(value);
      for (let key of keys) {
        const v = (value as {[key: string]: any})[key];
        if (typeof v === "object") {
          if (!!v.value) return null;
          errorLabels.push(v.label);
        }
      }
      // const someMessage = 
      //   prefix(label) +
      //   (errorLabels.length > 0 ? "以下の" : "") +
      //   ValidationErrorMessage[ValidationErrorCode.IS_EVERY_EMPTY] +
      //   (errorLabels.reduce((p: string, c: string) => p + "\n　・" + c, ""));
      const someMessage = ValidationErrorMessage[ValidationErrorCode.IS_EVERY_EMPTY];
      return { name, code: ValidationErrorCode.IS_EVERY_EMPTY, message: someMessage };
    }
  },
  {
    type: Validate.EVERY,
    callback: (value, name: string, label: string) => {
      if (!Array.isArray(value)) return null;
      const _value = (value || []) as any[];
      const result = _value.map((v: any) => {
        if (typeof v === "object") {
          return !!v.value ? null : { label: v.label };
        }
        return !!v ? null : { value: v };
      }).filter((v: any) => v);
      const errorLabels = result.reduce((p: string[], v: {
        label: string;
        value?: undefined;
      } | {
        value: SimpleValue;
        label?: undefined;
      }) => {
        if (!!v.label) p.push(v.label);
        return p;
      }, [] as string[]);
      const someMessage = 
        prefix(label) +
        (errorLabels.length > 0 ? "以下の" : "") +
        ValidationErrorMessage[ValidationErrorCode.IS_SOME_EMPTY] +
        (errorLabels.reduce((p: string, c: string) => p + "\n　・" + c, ""));
      return result.length > 0 ? { name, code: ValidationErrorCode.IS_SOME_EMPTY, message: someMessage } : null;
    }
  },
  {
    type: Validate.HIRAGANA,
    callback: (value, name: string, label: string) => {
      const reg = /^[\u3041-\u3094ー]+$/;
      if (typeof value !== "string" || !reg.test(value))
        return getValidationErrorMessage(ValidationErrorCode.INVALID_TYPE_AS_HIRAGANA, name, label);
      return null;
    }
  },
  {
    type: Validate.KATAKANA,
    callback: (value, name: string, label: string) => {
      const reg = new RegExp(/^[\u30A1-\u30FAー]+$/);
      if (typeof value !== "string" || !reg.test(value))
        return getValidationErrorMessage(ValidationErrorCode.INVALID_TYPE_AS_KATAKANA, name, label);
      return null;
    }
  }
];